import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, Button, Chip, Stack, TextField,
  IconButton, CircularProgress, Alert, Tooltip, List, ListItem, ListItemText,
  Paper, LinearProgress, Menu, MenuItem, Collapse, Divider, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import {
  ArrowBack as BackIcon, AutoAwesome as AiIcon, Mic as MicIcon, Stop as StopIcon,
  Save as SaveIcon, Delete as DeleteIcon, Add as AddIcon, Check as CheckIcon,
  Close as CloseIcon, Undo as UndoIcon, Send as SendIcon, Edit as EditIcon,
  Gavel as DecisionIcon, TipsAndUpdates as RecommendIcon, HelpOutline as QuestionIcon,
  Verified as ApproveIcon, ContentCopy as NewVersionIcon, AssignmentInd as AssignIcon,
  ChatBubbleOutline as CommentIcon, PlaylistAddCheck as EmbedIcon, Block as DeclineIcon,
  History as HistoryIcon, ExpandMore as ExpandIcon, Send as SubmitIcon,
  FormatQuote as CiteIcon, FactCheck as AdherenceIcon, Checklist as ChecklistIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import {
  getProtocol, getProtocolConfig, getChat, sendChat, transcribeChatVoice, applyChat, discardChat, undoProtocol,
  updateSection, addSection, deleteSection, addQuestion,
  getAssignees, assignQuestion, getComments, addComment, updateComment, deleteComment,
  embedQuestion, declineQuestion, getHistory,
  submitProtocol, returnToDraft, approveProtocol, newVersion, getReviewComments, addReviewComment,
  getUsage, recordingStart, recordingChunk, recordingFinalize,
  getChecklist, generateChecklist, addChecklistItem, updateChecklistItem, deleteChecklistItem,
} from '../api/protocols';

const STATE_COLOR = { draft: 'default', in_review: 'warning', published: 'success', processing: 'info', failed: 'error' };
const Q_COLOR = { open: 'default', assigned: 'info', resolved: 'success', declined: 'error' };

function fmtTime(s) { const m = Math.floor(s / 60), ss = String(s % 60).padStart(2, '0'); return `${m}:${ss}`; }
function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch { return iso; }
}

// ── Word-level inline diff (no dependency) ───────────────────────────────────
function tokenize(s) { return (s || '').split(/(\s+)/).filter((t) => t !== ''); }
function diffTokens(oldStr, newStr) {
  const a = tokenize(oldStr), b = tokenize(newStr);
  const n = a.length, m = b.length;
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) {
    dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  }
  const out = []; let i = 0, j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { out.push({ t: 'same', s: a[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push({ t: 'del', s: a[i] }); i++; }
    else { out.push({ t: 'add', s: b[j] }); j++; }
  }
  while (i < n) out.push({ t: 'del', s: a[i++] });
  while (j < m) out.push({ t: 'add', s: b[j++] });
  return out;
}
function DiffText({ oldText, newText }) {
  const toks = useMemo(() => diffTokens(oldText, newText), [oldText, newText]);
  return (
    <Box component="div" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, fontSize: '0.95rem' }}>
      {toks.map((t, i) => t.t === 'same'
        ? <span key={i}>{t.s}</span>
        : t.t === 'del'
          ? <Box key={i} component="span" sx={{ bgcolor: '#fde2e1', color: '#b3261e', textDecoration: 'line-through', borderRadius: 0.5 }}>{t.s}</Box>
          : <Box key={i} component="span" sx={{ bgcolor: '#d7f5e3', color: '#0f7b3f', borderRadius: 0.5 }}>{t.s}</Box>)}
    </Box>
  );
}

function DiffBlock({ edit, oldText, label, busy, onKeep, onDiscard }) {
  const isAdd = edit.op === 'add', isDelete = edit.op === 'delete';
  return (
    <Card variant="outlined" sx={{ mb: 2, borderColor: '#f59e0b', boxShadow: '0 0 0 1px #fde68a' }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <AiIcon color="primary" fontSize="small" />
            <Typography fontWeight={700}>{isAdd ? 'New section · ' : isDelete ? 'Remove · ' : ''}{label}</Typography>
            <Chip size="small" label={edit.op} variant="outlined" />
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="contained" color="success" startIcon={<CheckIcon />} disabled={busy} onClick={onKeep}>Keep</Button>
            <Button size="small" startIcon={<CloseIcon />} disabled={busy} onClick={onDiscard}>Discard</Button>
          </Stack>
        </Stack>
        <DiffText oldText={oldText} newText={isDelete ? '' : (edit.body || '')} />
        {edit.reason && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Why: {edit.reason}</Typography>}
      </CardContent>
    </Card>
  );
}

// ── Section with an explicit Edit button ─────────────────────────────────────
function SectionCard({ section, editable, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(section.title);
  const [body, setBody] = useState(section.body || '');
  useEffect(() => { setTitle(section.title); setBody(section.body || ''); setEditing(false); }, [section.id, section.title, section.body]);

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          {editing ? (
            <TextField value={title} onChange={(e) => setTitle(e.target.value)} variant="standard" fullWidth
              InputProps={{ sx: { fontWeight: 700, fontSize: '1.05rem' } }} />
          ) : (
            <Typography fontWeight={700} sx={{ flex: 1, fontSize: '1.05rem' }}>{section.title}</Typography>
          )}
          {editable && !editing && (
            <>
              <Tooltip title="Edit section"><IconButton size="small" onClick={() => setEditing(true)}><EditIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Delete section"><IconButton size="small" onClick={() => onDelete(section)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
            </>
          )}
        </Stack>
        {editing ? (
          <>
            <TextField value={body} onChange={(e) => setBody(e.target.value)} fullWidth multiline minRows={4} size="small" />
            <Box sx={{ mt: 1, textAlign: 'right' }}>
              <Button size="small" onClick={() => { setTitle(section.title); setBody(section.body || ''); setEditing(false); }}>Cancel</Button>
              <Button size="small" variant="contained" startIcon={<SaveIcon />} sx={{ ml: 1 }}
                onClick={async () => { await onSave(section, { title, body }); setEditing(false); }}>Save</Button>
            </Box>
          </>
        ) : (
          <>
            <Typography sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{section.body || <em style={{ color: '#94a3b8' }}>Empty</em>}</Typography>
            {(section.source_citations || []).length > 0 && (
              <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap' }} alignItems="center">
                <CiteIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                {section.source_citations.map((c, i) => (
                  <Tooltip key={i} title={c.locator || ''}><Chip size="small" variant="outlined" label={c.source || 'source'} /></Tooltip>
                ))}
              </Stack>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function UsagePanel({ protocolId }) {
  const [u, setU] = useState(null);
  useEffect(() => { getUsage(protocolId).then(setU).catch(() => {}); }, [protocolId]);
  if (!u) return null;
  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <AdherenceIcon fontSize="small" color="primary" /><Typography fontWeight={700}>Point-of-care usage</Typography>
      </Stack>
      {u.encounters === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Not yet used in an encounter. Once published, attach it to a patient visit to track adherence.
        </Typography>
      ) : (
        <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
          <Box><Typography variant="h5" fontWeight={800}>{u.encounters}</Typography><Typography variant="caption" color="text.secondary">encounters</Typography></Box>
          <Box><Typography variant="h5" fontWeight={800} color="success.main">{u.adherence_rate != null ? `${u.adherence_rate}%` : '—'}</Typography><Typography variant="caption" color="text.secondary">followed</Typography></Box>
          <Box><Typography variant="h5" fontWeight={800} color="warning.main">{u.deviated}</Typography><Typography variant="caption" color="text.secondary">deviations</Typography></Box>
          <Box><Typography variant="h5" fontWeight={800} color="text.secondary">{u.not_addressed}</Typography><Typography variant="caption" color="text.secondary">not addressed</Typography></Box>
        </Stack>
      )}
    </Paper>
  );
}

// ── Point-of-care questions ──────────────────────────────────────────────────
// The list a provider is prompted with while recording an encounter against this
// protocol, each turning green once the live listener hears it answered. Extracted
// from the protocol's prose when a version is published, and editable here — a
// prompt that reads badly in clinic is worth fixing without cutting a new version.
function ChecklistPanel({ protocolId }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newText, setNewText] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  const load = useCallback(() => {
    getChecklist(protocolId).then((d) => setItems(d.items || [])).catch(() => setItems([]));
  }, [protocolId]);
  useEffect(() => { load(); }, [load]);

  const regenerate = async () => {
    if (items?.length && !window.confirm('Replace the current questions with a fresh list from the protocol text? Any wording you have edited here will be lost.')) return;
    setBusy(true);
    try {
      const d = await generateChecklist(protocolId);
      setItems(d.items || []);
      toast.success(`Generated ${(d.items || []).length} question(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate the questions');
    } finally { setBusy(false); }
  };

  const add = async () => {
    const text = newText.trim();
    if (!text) return;
    try { await addChecklistItem(protocolId, { text }); setNewText(''); load(); }
    catch { toast.error('Could not add the question'); }
  };

  const saveEdit = async (item) => {
    const text = editText.trim();
    if (!text) return;
    try { await updateChecklistItem(protocolId, item.id, { text }); setEditingId(null); load(); }
    catch { toast.error('Could not save the question'); }
  };

  const remove = async (item) => {
    try { await deleteChecklistItem(protocolId, item.id); load(); }
    catch { toast.error('Could not remove the question'); }
  };

  if (items === null) return null;
  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <ChecklistIcon fontSize="small" color="primary" />
        <Typography fontWeight={700}>Questions to ask at the visit</Typography>
        <Box sx={{ flex: 1 }} />
        <Button size="small" startIcon={busy ? <CircularProgress size={14} /> : <AiIcon />} disabled={busy} onClick={regenerate}>
          {items.length ? 'Regenerate' : 'Generate'}
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Shown in the recording sidebar during an encounter. Generating or regenerating uses AI
        credits; typing and editing them by hand doesn't. Providers can also choose to have the
        AI tick them off during the visit, which uses credits every visit it's on.
      </Typography>
      {items.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          None yet — they're extracted automatically when this version is published, or generate them now.
        </Typography>
      )}
      {items.map((it, n) => (
        <Box key={it.id} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', py: 0.75, borderTop: n ? '1px solid' : 'none', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary" sx={{ pt: 0.5, minWidth: 20 }}>{n + 1}.</Typography>
          {editingId === it.id ? (
            <>
              <TextField size="small" fullWidth multiline value={editText} onChange={(e) => setEditText(e.target.value)} />
              <IconButton size="small" color="primary" onClick={() => saveEdit(it)}><SaveIcon fontSize="small" /></IconButton>
              <IconButton size="small" onClick={() => setEditingId(null)}><CloseIcon fontSize="small" /></IconButton>
            </>
          ) : (
            <>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2">{it.text}</Typography>
                {it.hint && <Typography variant="caption" color="text.secondary">{it.hint}</Typography>}
              </Box>
              <IconButton size="small" onClick={() => { setEditingId(it.id); setEditText(it.text); }}><EditIcon fontSize="small" /></IconButton>
              <IconButton size="small" onClick={() => remove(it)}><DeleteIcon fontSize="small" /></IconButton>
            </>
          )}
        </Box>
      ))}
      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
        <TextField size="small" fullWidth placeholder="Add a question to ask" value={newText}
          onChange={(e) => setNewText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} />
        <IconButton color="primary" onClick={add}><AddIcon /></IconButton>
      </Stack>
    </Paper>
  );
}

// ── Open-question item with assign / comments / embed / decline ──────────────
function QuestionItem({ q, protocolId, assignees, sectionTitles, canResolve, editable, currentUserId, onChanged }) {
  const [menuEl, setMenuEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [comments, setComments] = useState(null);
  const [comment, setComment] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [embedOpen, setEmbedOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const assignee = assignees.find((u) => u.id === q.assignee_id);
  const closed = q.status === 'resolved' || q.status === 'declined';

  const loadComments = useCallback(async () => {
    try { const d = await getComments(protocolId, q.id); setComments(d.comments || []); } catch { setComments([]); }
  }, [protocolId, q.id]);

  const toggle = () => { const n = !open; setOpen(n); if (n && comments === null) loadComments(); };

  const assign = async (userId) => {
    setMenuEl(null); setBusy(true);
    try { await assignQuestion(protocolId, q.id, userId); toast.success('Assigned — email sent'); onChanged(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Could not assign'); }
    finally { setBusy(false); }
  };
  const postComment = async () => {
    if (!comment.trim()) return;
    try { await addComment(protocolId, q.id, comment.trim()); setComment(''); await loadComments(); onChanged(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Could not comment'); }
  };

  const startEdit = (c) => { setEditingId(c.id); setEditText(c.body); };
  const cancelEdit = () => { setEditingId(null); setEditText(''); };
  const saveEdit = async (c) => {
    const next = editText.trim();
    if (!next) return;
    if (next === c.body) { cancelEdit(); return; }
    try { await updateComment(protocolId, q.id, c.id, next); cancelEdit(); await loadComments(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Could not save comment'); }
  };
  const removeComment = async (c) => {
    if (!window.confirm('Delete this comment? This cannot be undone.')) return;
    try { await deleteComment(protocolId, q.id, c.id); await loadComments(); onChanged(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Could not delete comment'); }
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1, opacity: closed ? 0.7 : 1 }}>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ textDecoration: q.status === 'declined' ? 'line-through' : 'none' }}>{q.question}</Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
            <Chip size="small" label={q.status} color={Q_COLOR[q.status] || 'default'} />
            {assignee && <Chip size="small" variant="outlined" avatar={<Avatar>{(assignee.name || '?').charAt(0)}</Avatar>} label={assignee.name} />}
            <Button size="small" startIcon={<CommentIcon sx={{ fontSize: 16 }} />} onClick={toggle}>
              {q.comment_count > 0 ? `${q.comment_count}` : ''} {open ? 'Hide' : 'Discuss'}
            </Button>
          </Stack>
        </Box>
        {editable && !closed && (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Assign to a colleague">
              <span><IconButton size="small" disabled={busy} onClick={(e) => setMenuEl(e.currentTarget)}><AssignIcon fontSize="small" /></IconButton></span>
            </Tooltip>
            {canResolve(q) && (
              <>
                <Tooltip title="Embed answer into protocol"><IconButton size="small" color="success" onClick={() => setEmbedOpen(true)}><EmbedIcon fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Decline"><IconButton size="small" onClick={() => setDeclineOpen(true)}><DeclineIcon fontSize="small" /></IconButton></Tooltip>
              </>
            )}
          </Stack>
        )}
      </Stack>

      {q.status === 'declined' && q.declined_reason && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>Declined: {q.declined_reason}</Typography>
      )}

      <Menu anchorEl={menuEl} open={Boolean(menuEl)} onClose={() => setMenuEl(null)}>
        {assignees.length === 0 && <MenuItem disabled>No colleagues</MenuItem>}
        {assignees.map((u) => <MenuItem key={u.id} onClick={() => assign(u.id)}>{u.name} <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>{u.role}</Typography></MenuItem>)}
      </Menu>

      <Collapse in={open}>
        <Divider sx={{ my: 1 }} />
        {comments === null ? <CircularProgress size={16} /> : comments.length === 0 ? (
          <Typography variant="caption" color="text.secondary">No comments yet.</Typography>
        ) : comments.map((c) => {
          const mine = Boolean(c.author_id && c.author_id === currentUserId);
          const editing = editingId === c.id;
          return (
            <Box key={c.id} sx={{ mb: 0.75 }}>
              <Stack direction="row" alignItems="center" spacing={0.25}>
                <Typography variant="caption" fontWeight={700} sx={{ flex: 1 }}>
                  {c.author}
                  <span style={{ fontWeight: 400, color: '#94a3b8' }}>
                    {' '}· {fmtDate(c.created_at)}{c.edited_at ? ' · (edited)' : ''}
                  </span>
                </Typography>
                {mine && !editing && (
                  <>
                    <Tooltip title="Edit comment">
                      <IconButton size="small" onClick={() => startEdit(c)}><EditIcon sx={{ fontSize: 14 }} /></IconButton>
                    </Tooltip>
                    <Tooltip title="Delete comment">
                      <IconButton size="small" onClick={() => removeComment(c)}><DeleteIcon sx={{ fontSize: 14 }} /></IconButton>
                    </Tooltip>
                  </>
                )}
              </Stack>
              {editing ? (
                <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ mt: 0.5 }}>
                  <TextField size="small" fullWidth multiline autoFocus value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEdit(c); }
                      if (e.key === 'Escape') cancelEdit();
                    }} />
                  <Tooltip title="Save">
                    <span><IconButton size="small" color="primary" disabled={!editText.trim()} onClick={() => saveEdit(c)}><CheckIcon fontSize="small" /></IconButton></span>
                  </Tooltip>
                  <Tooltip title="Cancel">
                    <IconButton size="small" onClick={cancelEdit}><CloseIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </Stack>
              ) : (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{c.body}</Typography>
              )}
            </Box>
          );
        })}
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <TextField size="small" fullWidth placeholder="Add a comment" value={comment}
            onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && postComment()} />
          <IconButton color="primary" onClick={postComment}><SendIcon fontSize="small" /></IconButton>
        </Stack>
      </Collapse>

      <EmbedDialog open={embedOpen} onClose={() => setEmbedOpen(false)} question={q} sectionTitles={sectionTitles}
        onEmbed={async (payload) => {
          try { await embedQuestion(protocolId, q.id, payload); toast.success('Embedded into protocol'); setEmbedOpen(false); onChanged(); }
          catch (err) { toast.error(err.response?.data?.detail || 'Could not embed'); }
        }} />
      <DeclineDialog open={declineOpen} onClose={() => setDeclineOpen(false)}
        onDecline={async (reason) => {
          try { await declineQuestion(protocolId, q.id, reason); toast.success('Declined'); setDeclineOpen(false); onChanged(); }
          catch (err) { toast.error(err.response?.data?.detail || 'Could not decline'); }
        }} />
    </Paper>
  );
}

function EmbedDialog({ open, onClose, question, sectionTitles, onEmbed }) {
  const [target, setTarget] = useState('__new__');
  const [answer, setAnswer] = useState('');
  useEffect(() => { if (open) { setTarget('__new__'); setAnswer(question?.resolution || ''); } }, [open, question]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle fontWeight={700}>Embed answer into the protocol</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{question?.question}</Typography>
        <TextField select fullWidth label="Where" value={target} onChange={(e) => setTarget(e.target.value)} sx={{ mb: 2 }}>
          <MenuItem value="__new__">Create a new section</MenuItem>
          {sectionTitles.map((t) => <MenuItem key={t} value={t}>Append to “{t}”</MenuItem>)}
        </TextField>
        <TextField fullWidth multiline minRows={4} label="Answer to embed" value={answer} onChange={(e) => setAnswer(e.target.value)} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="success" startIcon={<EmbedIcon />} disabled={!answer.trim()}
          onClick={() => onEmbed({ body: answer.trim(), section_title: target === '__new__' ? null : target })}>Embed</Button>
      </DialogActions>
    </Dialog>
  );
}

function DeclineDialog({ open, onClose, onDecline }) {
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) setReason(''); }, [open]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle fontWeight={700}>Decline this question</DialogTitle>
      <DialogContent>
        <TextField autoFocus fullWidth multiline minRows={3} label="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} sx={{ mt: 1 }} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="error" startIcon={<DeclineIcon />} onClick={() => onDecline(reason.trim())}>Decline</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Submit for review (pick a reviewer) ──────────────────────────────────────
function SubmitDialog({ open, onClose, assignees, currentUserId, onSubmit }) {
  const [reviewer, setReviewer] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setReviewer(''); }, [open]);
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle fontWeight={700}>Submit for review</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Assign an active colleague to review. They’ll get an email and can edit, comment, approve, or send it back to draft.
        </Typography>
        <TextField select fullWidth label="Reviewer" value={reviewer} onChange={(e) => setReviewer(e.target.value)}>
          {assignees.length === 0 && <MenuItem disabled>No eligible reviewers in this clinic</MenuItem>}
          {assignees.map((u) => (
            <MenuItem key={u.id} value={u.id}>{u.name}
              <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>{u.role}{u.id === currentUserId ? ' · you' : ''}</Typography>
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" disabled={busy || !reviewer} startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <SubmitIcon />}
          onClick={async () => { setBusy(true); await onSubmit(reviewer); setBusy(false); }}>Submit</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Review comments (version-level, with @mentions) ──────────────────────────
function ReviewCommentsDialog({ open, onClose, protocolId, assignees, editable, onPosted }) {
  const [comments, setComments] = useState(null);
  const [body, setBody] = useState('');
  const [mentions, setMentions] = useState([]);
  const [menuEl, setMenuEl] = useState(null);
  const load = useCallback(async () => {
    try { const d = await getReviewComments(protocolId); setComments(d.comments || []); } catch { setComments([]); }
  }, [protocolId]);
  useEffect(() => { if (open) { setBody(''); setMentions([]); load(); } }, [open, load]);
  const nameOf = (uid) => assignees.find((u) => u.id === uid)?.name || 'user';
  const post = async () => {
    if (!body.trim()) return;
    try { await addReviewComment(protocolId, body.trim(), mentions); setBody(''); setMentions([]); await load(); onPosted && onPosted(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Could not comment'); }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle fontWeight={700}>Review comments</DialogTitle>
      <DialogContent dividers>
        {comments === null ? <CircularProgress size={20} /> : comments.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No comments yet.</Typography>
        ) : comments.map((c) => (
          <Box key={c.id} sx={{ mb: 1.5 }}>
            <Typography variant="caption" fontWeight={700}>{c.author} <span style={{ fontWeight: 400, color: '#94a3b8' }}>· {fmtDate(c.created_at)}</span></Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{c.body}</Typography>
            {(c.mentions || []).length > 0 && (
              <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                {c.mentions.map((m) => <Chip key={m} size="small" label={`@${nameOf(m)}`} />)}
              </Stack>
            )}
          </Box>
        ))}
      </DialogContent>
      {editable && (
        <Box sx={{ p: 2 }}>
          {mentions.length > 0 && (
            <Stack direction="row" spacing={0.5} sx={{ mb: 1, flexWrap: 'wrap' }}>
              {mentions.map((m) => <Chip key={m} size="small" label={`@${nameOf(m)}`} onDelete={() => setMentions((x) => x.filter((i) => i !== m))} />)}
            </Stack>
          )}
          <Stack direction="row" spacing={1} alignItems="flex-end">
            <Tooltip title="Tag a colleague"><IconButton onClick={(e) => setMenuEl(e.currentTarget)}><AssignIcon /></IconButton></Tooltip>
            <TextField size="small" fullWidth multiline maxRows={4} placeholder="Add a comment…" value={body} onChange={(e) => setBody(e.target.value)} />
            <IconButton color="primary" disabled={!body.trim()} onClick={post}><SendIcon /></IconButton>
          </Stack>
          <Menu anchorEl={menuEl} open={Boolean(menuEl)} onClose={() => setMenuEl(null)}>
            {assignees.length === 0 && <MenuItem disabled>No colleagues</MenuItem>}
            {assignees.map((u) => <MenuItem key={u.id} onClick={() => { setMentions((x) => x.includes(u.id) ? x : [...x, u.id]); setMenuEl(null); }}>{u.name}</MenuItem>)}
          </Menu>
        </Box>
      )}
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

// ── Audit trail ──────────────────────────────────────────────────────────────
function HistoryPanel({ events }) {
  return (
    <Accordion variant="outlined" sx={{ mt: 2 }}>
      <AccordionSummary expandIcon={<ExpandIcon />}>
        <Stack direction="row" spacing={1} alignItems="center"><HistoryIcon fontSize="small" color="primary" /><Typography fontWeight={700}>History &amp; audit trail</Typography></Stack>
      </AccordionSummary>
      <AccordionDetails>
        {(!events || events.length === 0) ? <Typography variant="body2" color="text.secondary">No activity yet.</Typography> : (
          <List dense disablePadding>
            {events.map((e) => (
              <ListItem key={e.id} disableGutters sx={{ opacity: e.undone ? 0.5 : 1 }}>
                <ListItemText
                  primary={<Typography variant="body2"><b>{e.actor}</b> — {e.label}{e.target ? ` · ${e.target}` : ''}{e.undone ? ' (reverted)' : ''}</Typography>}
                  secondary={<Typography variant="caption" color="text.secondary">{fmtDate(e.created_at)}{e.detail ? ` — ${e.detail}` : ''}</Typography>} />
              </ListItem>
            ))}
          </List>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

// ── Chat panel (controlled input + Keep all/Discard all) ─────────────────────
function ChatBubble({ m, activeCount, onReviewClick }) {
  const isUser = m.role === 'user';
  const hasProposal = !isUser && (m.proposal || []).length > 0;
  const resolvedLabel = m.status === 'superseded' ? 'superseded by a newer suggestion'
    : m.status === 'discarded' ? 'changes discarded'
    : m.status === 'applied' ? 'changes kept' : 'changes resolved';
  return (
    <Box sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', mb: 1 }}>
      <Box sx={{ maxWidth: '85%', px: 1.5, py: 1, borderRadius: 2, bgcolor: isUser ? 'primary.main' : 'rgba(4,47,46,0.05)', color: isUser ? '#fff' : 'text.primary' }}>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.content}</Typography>
        {hasProposal && (
          activeCount > 0 ? (
            <Chip size="small" clickable onClick={onReviewClick} sx={{ mt: 0.5 }} color="warning"
              label={`${activeCount} change(s) to review →`} />
          ) : (
            <Chip size="small" sx={{ mt: 0.5 }} color="default" label={resolvedLabel} />
          )
        )}
      </Box>
    </Box>
  );
}

function ChatPanel({ messages, sending, value, onChange, onSend, undoAvailable, onUndo, busy, activeCountFor, activeCount, onKeepAll, onDiscardAll, inputRef, protocolId, onReviewClick }) {
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, sending]);
  const submit = () => { const t = (value || '').trim(); if (t) onSend(t); };

  // ── Dictation: speak instead of typing. The transcript lands in the box for the
  // user to read and correct; it is never sent to the AI on their behalf.
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const valueRef = useRef(value);   // onstop fires outside this render — avoid a stale closure
  valueRef.current = value;

  const releaseMic = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };
  // Never leave the microphone live if the panel unmounts mid-recording.
  useEffect(() => () => {
    try { if (recorderRef.current?.state === 'recording') recorderRef.current.stop(); } catch { /* already stopped */ }
    streamRef.current?.getTracks().forEach((t) => t.stop());
  }, []);

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error('Dictation needs a secure (https) connection');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const rec = new MediaRecorder(stream, MediaRecorder.isTypeSupported('audio/webm') ? { mimeType: 'audio/webm' } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        releaseMic();
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        chunksRef.current = [];
        if (!blob.size) return;
        setTranscribing(true);
        try {
          const { text } = await transcribeChatVoice(protocolId, blob);
          const existing = (valueRef.current || '').trim();
          onChange(existing ? `${existing} ${text}` : text);   // append to anything already typed
          inputRef?.current?.focus();
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Could not transcribe the recording');
        } finally {
          setTranscribing(false);
        }
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch {
      releaseMic();
      toast.error('Could not access the microphone');
    }
  };

  const stopRecording = () => {
    setRecording(false);
    try { recorderRef.current?.stop(); } catch { releaseMic(); }
  };
  return (
    <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 180px)', position: 'sticky', top: 88 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 1.5, borderBottom: '1px solid rgba(4,47,46,0.08)' }}>
        <Stack direction="row" spacing={1} alignItems="center"><AiIcon color="primary" /><Typography fontWeight={700}>AI co-author</Typography></Stack>
        <Tooltip title="Undo the last kept change"><span><Button size="small" startIcon={<UndoIcon />} disabled={!undoAvailable || busy} onClick={onUndo}>Undo</Button></span></Tooltip>
      </Stack>
      {activeCount > 0 && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1, bgcolor: '#fff7ed', borderBottom: '1px solid #fed7aa' }}>
          <Typography variant="caption" sx={{ flex: 1 }}>{activeCount} suggestion(s) in the document</Typography>
          <Button size="small" color="success" variant="contained" disabled={busy} onClick={onKeepAll}>Keep all</Button>
          <Button size="small" color="inherit" onClick={onDiscardAll}>Discard all</Button>
        </Stack>
      )}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
        {messages.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Ask me to draft, tighten, or add to this protocol. Select text in the document to add it here as context.
          </Typography>
        )}
        {messages.map((m) => <ChatBubble key={m.id} m={m} activeCount={activeCountFor(m)} onReviewClick={onReviewClick} />)}
        {sending && <Stack direction="row" spacing={1} sx={{ mt: 1 }}><CircularProgress size={16} /><Typography variant="body2" color="text.secondary">Thinking…</Typography></Stack>}
        <div ref={endRef} />
      </Box>
      <Box sx={{ p: 1.5, borderTop: '1px solid rgba(4,47,46,0.08)' }}>
        <Stack direction="row" spacing={1} alignItems="flex-end">
          <TextField size="small" fullWidth multiline maxRows={5} value={value} inputRef={inputRef}
            placeholder={recording ? 'Listening — speak now…' : transcribing ? 'Transcribing…' : 'Message the AI, or tap the mic to speak…'}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }} />
          <Tooltip title={recording ? 'Stop and transcribe' : 'Dictate a message'}>
            <span>
              <IconButton
                color={recording ? 'error' : 'default'}
                disabled={sending || transcribing}
                onClick={recording ? stopRecording : startRecording}
              >
                {transcribing ? <CircularProgress size={20} /> : recording ? <StopIcon /> : <MicIcon />}
              </IconButton>
            </span>
          </Tooltip>
          <IconButton color="primary" disabled={sending || recording || transcribing || !(value || '').trim()} onClick={submit}><SendIcon /></IconButton>
        </Stack>
        {recording && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
            Recording… tap stop when you're done. You can edit the text before sending.
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

export default function ProtocolWorkbench() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isClinicAdmin = user?.role === 'clinic_admin';
  // `sub` was the JWT's subject; the profile from /auth/me calls the same thing `id`.
  const currentUserId = user?.id || user?.sub;

  const [protocol, setProtocol] = useState(null);
  const [config, setConfig] = useState(null);
  const [messages, setMessages] = useState([]);
  const [history, setHistory] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [undoAvailable, setUndoAvailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [discarded, setDiscarded] = useState(new Set());
  const [newQuestion, setNewQuestion] = useState('');
  const [chatText, setChatText] = useState('');
  const [submitOpen, setSubmitOpen] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [reviewComments, setReviewComments] = useState([]);
  const [sel, setSel] = useState(null);
  const chatInputRef = useRef(null);
  const docRef = useRef(null);
  const reviewRef = useRef(null);   // the "N suggestions below" banner — scroll target for the chat CTA

  // Scroll the document to where the pending edits begin (item 20).
  const scrollToReview = useCallback(() => {
    const el = reviewRef.current || docRef.current;
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // capture
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const recorderRef = useRef(null);
  const seqRef = useRef(0);
  const capTimerRef = useRef(null);
  const tickRef = useRef(null);
  const pollRef = useRef(null);

  const head = protocol?.head_version || null;
  const editable = head && ['draft', 'failed', 'in_review'].includes(head.state);

  const reloadAll = useCallback(async () => {
    const [pd, ch, hi, rc] = await Promise.all([
      getProtocol(id), getChat(id),
      getHistory(id).catch(() => ({ events: [] })),
      getReviewComments(id).catch(() => ({ comments: [] })),
    ]);
    setProtocol(pd);
    setMessages(ch.messages || []);
    setHistory(hi.events || []);
    setReviewComments(rc.comments || []);
    setUndoAvailable(pd.undo_available ?? ch.undo_available ?? false);
    return pd;
  }, [id]);

  const startPolling = useCallback(() => {
    setProcessing(true);
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const pd = await getProtocol(id);
        if (pd.head_version?.state !== 'processing') {
          clearInterval(pollRef.current); setProcessing(false); await reloadAll();
          if (pd.head_version?.state === 'failed') toast.error('Processing failed — try again');
          else toast.success('Draft ready');
        }
      } catch { /* keep polling */ }
    }, 4000);
  }, [id, reloadAll]);

  useEffect(() => {
    (async () => {
      try {
        const pd = await reloadAll();
        if (pd.head_version?.state === 'processing') startPolling();
        getAssignees(id).then((d) => setAssignees(d.users || [])).catch(() => {});
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Could not load protocol'); navigate('/protocols');
      } finally { setLoading(false); }
    })();
    getProtocolConfig().then(setConfig).catch(() => {});
    return () => {
      clearInterval(tickRef.current); clearInterval(pollRef.current); clearTimeout(capTimerRef.current);
      if (recorderRef.current && recorderRef.current.state !== 'inactive') recorderRef.current.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ── selection → add to chat ────────────────────────────────────────────────
  useEffect(() => {
    const clear = () => setSel(null);
    window.addEventListener('scroll', clear, true);
    return () => window.removeEventListener('scroll', clear, true);
  }, []);
  const onDocMouseUp = () => {
    const s = window.getSelection();
    const txt = s?.toString().trim();
    if (txt && txt.length > 1 && docRef.current && s.anchorNode && docRef.current.contains(s.anchorNode)) {
      const rect = s.getRangeAt(0).getBoundingClientRect();
      setSel({ text: txt, x: rect.left + rect.width / 2, y: rect.top - 6 });
    } else setSel(null);
  };
  const addSelToChat = () => {
    setChatText((prev) => `${prev ? prev + '\n' : ''}> ${sel.text}\n`);
    setSel(null);
    setTimeout(() => chatInputRef.current?.focus(), 0);
  };

  const sectionsByTitle = useMemo(() => {
    const map = new Map();
    (head?.sections || []).forEach((s) => map.set((s.title || '').trim().toLowerCase(), s));
    return map;
  }, [head]);

  const isActionable = useCallback((e) => {
    const t = (e.section_title || '').trim().toLowerCase();
    if (e.op === 'summary') return (head?.summary || '') !== (e.body || '');
    if (e.op === 'add') return !sectionsByTitle.has(t);
    const sec = sectionsByTitle.get(t);
    if (e.op === 'delete') return !!sec;
    if (e.op === 'update') return sec && (sec.body || '') !== (e.body || '');
    return false;
  }, [head, sectionsByTitle]);

  const proposalMsg = useMemo(() => {
    // Only a pending proposal is reviewable. Superseded / discarded / applied ones
    // stay in the transcript but are not the active set.
    const withEdits = messages.filter((m) => m.role === 'assistant' && m.status === 'pending' && (m.proposal || []).length > 0);
    return withEdits.length ? withEdits[withEdits.length - 1] : null;
  }, [messages]);
  useEffect(() => { setDiscarded(new Set()); }, [proposalMsg?.id]);

  const activeEdits = useMemo(() => {
    if (!proposalMsg) return [];
    return (proposalMsg.proposal || []).map((e, i) => ({ ...e, _idx: i })).filter((e) => isActionable(e) && !discarded.has(e._idx));
  }, [proposalMsg, isActionable, discarded]);
  const activeCountFor = useCallback((m) => (!proposalMsg || m.id !== proposalMsg.id) ? 0 : activeEdits.length, [proposalMsg, activeEdits]);

  // ── chat ───────────────────────────────────────────────────────────────────
  const send = async (message) => {
    setSending(true); setChatText('');
    setMessages((ms) => [...ms, { id: `tmp-${Date.now()}`, role: 'user', content: message, proposal: [], status: 'none' }]);
    try { await sendChat(id, message); await reloadAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'AI request failed'); await reloadAll().catch(() => {}); }
    finally { setSending(false); }
  };
  const keepEdits = async (edits) => {
    if (!proposalMsg) return;
    setBusy(true);
    try {
      const res = await applyChat(id, proposalMsg.id, edits.map(({ _idx, ...e }) => e));
      if (res.head_version) setProtocol((p) => ({ ...p, head_version: res.head_version }));
      setUndoAvailable(res.undo_available ?? true);
      await Promise.all([getChat(id).then((ch) => setMessages(ch.messages || [])), getHistory(id).then((h) => setHistory(h.events || []))]).catch(() => {});
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not apply'); await reloadAll().catch(() => {}); }
    finally { setBusy(false); }
  };
  const discardEdit = (edit) => setDiscarded((d) => new Set(d).add(edit._idx));
  const discardAll = async () => { if (!proposalMsg) return; try { await discardChat(id, proposalMsg.id); await getChat(id).then((ch) => setMessages(ch.messages || [])); } catch (err) { toast.error(err.response?.data?.detail || 'Could not discard'); } };
  const undo = async () => {
    setBusy(true);
    try { const res = await undoProtocol(id); if (res.head_version) setProtocol((p) => ({ ...p, head_version: res.head_version })); setUndoAvailable(res.undo_available ?? false); await getHistory(id).then((h) => setHistory(h.events || [])); toast.success('Reverted'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Nothing to undo'); } finally { setBusy(false); }
  };

  // ── manual edits / questions / lifecycle ────────────────────────────────────
  const saveSection = async (section, payload) => { try { await updateSection(id, section.id, payload); await reloadAll(); toast.success('Saved'); } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); } };
  const removeSection = async (section) => { try { await deleteSection(id, section.id); await reloadAll(); } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); } };
  const createSection = async () => { try { await addSection(id, { title: 'New section', body: '' }); await reloadAll(); } catch (err) { toast.error(err.response?.data?.detail || 'Could not add section'); } };
  const submitQuestion = async () => { if (!newQuestion.trim()) return; try { await addQuestion(id, { question: newQuestion.trim() }); setNewQuestion(''); await reloadAll(); } catch (err) { toast.error(err.response?.data?.detail || 'Could not add question'); } };
  const doLifecycle = async (fn, okMsg) => { setBusy(true); try { await fn(id); await reloadAll(); toast.success(okMsg); } catch (err) { toast.error(err.response?.data?.detail || 'Action failed'); } finally { setBusy(false); } };

  // ── capture recording (chunked) ─────────────────────────────────────────────
  const startRecording = async () => {
    try {
      await recordingStart(id);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, MediaRecorder.isTypeSupported('audio/webm') ? { mimeType: 'audio/webm' } : undefined);
      seqRef.current = 0;
      rec.ondataavailable = async (e) => { if (e.data && e.data.size > 0) { try { await recordingChunk(id, seqRef.current++, e.data); } catch { /* resend next slice */ } } };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop()); clearInterval(tickRef.current); clearTimeout(capTimerRef.current);
        try { await recordingFinalize(id); startPolling(); } catch (err) { toast.error(err.response?.data?.detail || 'Could not process recording'); }
      };
      rec.start(15000); recorderRef.current = rec; setRecording(true); setElapsed(0);
      tickRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
      const capMin = config?.max_session_minutes || 120;
      capTimerRef.current = setTimeout(() => { toast('Recording cap reached — wrapping up'); stopRecording(); }, capMin * 60 * 1000);
    } catch { toast.error('Microphone access is required to record'); }
  };
  const stopRecording = () => { if (recorderRef.current && recorderRef.current.state !== 'inactive') recorderRef.current.stop(); setRecording(false); };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  if (!protocol) return null;

  const sections = head?.sections || [];
  const decisions = head?.decisions || [];
  const recommendations = head?.recommendations || [];
  const openQs = head?.open_questions || [];
  const sectionTitles = sections.map((s) => s.title);
  const isCapture = head?.source_mode === 'capture';
  const canResolve = (q) => isClinicAdmin || (q.assignee_id && q.assignee_id === currentUserId);
  // Approving is the assigned reviewer's act — clinic admins get no override.
  const canReview = Boolean(head?.reviewer_id && head.reviewer_id === currentUserId);
  const reviewerName = assignees.find((u) => u.id === head?.reviewer_id)?.name;

  const summaryEdit = activeEdits.find((e) => e.op === 'summary');
  const editByTitle = (title) => activeEdits.find((e) => (e.op === 'update' || e.op === 'delete') && (e.section_title || '').trim().toLowerCase() === (title || '').trim().toLowerCase());
  const addEdits = activeEdits.filter((e) => e.op === 'add');
  const reviewing = activeEdits.length > 0;

  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
      <Button startIcon={<BackIcon />} onClick={() => navigate('/protocols')} sx={{ mb: 1 }}>Protocols</Button>

      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>{protocol.name}</Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
            <Chip size="small" label={head?.version || 'v1'} variant="outlined" />
            <Chip size="small" label={(head?.state || 'draft').replace('_', ' ')} color={STATE_COLOR[head?.state] || 'default'} />
            {isCapture && <Chip size="small" icon={<MicIcon />} label="capture" variant="outlined" />}
          </Stack>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {head?.state !== 'published' && (
            <Button startIcon={<CommentIcon />} onClick={() => setCommentsOpen(true)}>
              Comments{reviewComments.length ? ` (${reviewComments.length})` : ''}
            </Button>
          )}
          {(head?.state === 'draft' || head?.state === 'failed') && (
            <Button variant="outlined" startIcon={<SubmitIcon />} disabled={busy} onClick={() => setSubmitOpen(true)}>Submit for review</Button>
          )}
          {head?.state === 'in_review' && (
            <Button variant="outlined" startIcon={<UndoIcon />} disabled={busy} onClick={() => doLifecycle(returnToDraft, 'Returned to draft')}>Return to draft</Button>
          )}
          {head?.state === 'in_review' && canReview && (
            <Button variant="contained" color="success" startIcon={<ApproveIcon />} disabled={busy} onClick={() => doLifecycle(approveProtocol, 'Published')}>Approve &amp; publish</Button>
          )}
          {head?.state === 'published' && (
            <Button variant="outlined" startIcon={<NewVersionIcon />} disabled={busy} onClick={() => doLifecycle(newVersion, 'New version started')}>Start new version</Button>
          )}
        </Stack>
      </Stack>

      {head?.state === 'in_review' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          In review{reviewerName ? ` — assigned to ${reviewerName}` : ''}.{' '}
          {canReview
            ? 'You are the assigned reviewer — you can edit, comment, approve, or return it to draft.'
            : 'Only the assigned reviewer can approve it. You can still edit, comment, or return it to draft.'}
        </Alert>
      )}
      {head?.state === 'published' && <Alert severity="success" sx={{ mb: 2 }}>This version is published and read-only. Start a new version to make changes.</Alert>}

      {(isCapture && (sections.length === 0 || recording || processing)) && (
        <Card variant="outlined" sx={{ mb: 2, borderColor: 'primary.light', bgcolor: 'rgba(13,148,136,0.04)' }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={2}>
              <MicIcon color="primary" />
              <Box sx={{ flex: 1 }}>
                <Typography fontWeight={700}>Capture the working session</Typography>
                <Typography variant="body2" color="text.secondary">Record the discussion — the AI drafts the protocol from it. Chunks upload as you go (max {config?.max_session_minutes || 120} min).</Typography>
              </Box>
              {processing ? <Stack alignItems="center" spacing={0.5}><CircularProgress size={22} /><Typography variant="caption">Transcribing…</Typography></Stack>
                : recording ? <Stack alignItems="center" spacing={0.5}><Button variant="contained" color="error" startIcon={<StopIcon />} onClick={stopRecording}>Stop</Button><Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtTime(elapsed)}</Typography></Stack>
                  : <Button variant="contained" startIcon={<MicIcon />} onClick={startRecording} disabled={!editable}>Record</Button>}
            </Stack>
            {recording && <LinearProgress sx={{ mt: 2 }} />}
          </CardContent>
        </Card>
      )}

      <Grid container spacing={2}>
        {/* Document */}
        <Grid item xs={12} md={8}>
          <Box ref={docRef} onMouseUp={onDocMouseUp}>
            {reviewing && (
              <Alert ref={reviewRef} severity="warning" sx={{ mb: 2, scrollMarginTop: '88px' }} icon={<AiIcon />}
                action={<Stack direction="row" spacing={1}><Button size="small" color="success" variant="contained" disabled={busy} onClick={() => keepEdits(activeEdits)}>Keep all</Button><Button size="small" color="inherit" onClick={discardAll}>Discard all</Button></Stack>}>
                {activeEdits.length} AI suggestion(s) below — review each, then Keep or Discard.
              </Alert>
            )}

            {summaryEdit ? (
              <DiffBlock edit={summaryEdit} oldText={head.summary || ''} label="Protocol Draft" busy={busy} onKeep={() => keepEdits([summaryEdit])} onDiscard={() => discardEdit(summaryEdit)} />
            ) : head?.summary && (
              <Card variant="outlined" sx={{ mb: 2 }}><CardContent><Typography variant="overline" color="text.secondary">Protocol Draft</Typography><Typography sx={{ whiteSpace: 'pre-wrap' }}>{head.summary}</Typography></CardContent></Card>
            )}

            {sections.map((s) => {
              const e = editByTitle(s.title);
              if (e) return <DiffBlock key={s.id} edit={e} oldText={s.body || ''} label={s.title} busy={busy} onKeep={() => keepEdits([e])} onDiscard={() => discardEdit(e)} />;
              return <SectionCard key={s.id} section={s} editable={editable && !reviewing} onSave={saveSection} onDelete={removeSection} />;
            })}

            {addEdits.map((e) => <DiffBlock key={`add-${e._idx}`} edit={e} oldText="" label={e.section_title || 'New section'} busy={busy} onKeep={() => keepEdits([e])} onDiscard={() => discardEdit(e)} />)}
          </Box>

          {editable && !reviewing && <Button startIcon={<AddIcon />} onClick={createSection} variant="outlined" sx={{ mt: 0.5, mb: 3 }}>Add section</Button>}

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}><DecisionIcon fontSize="small" color="primary" /><Typography fontWeight={700}>Decisions</Typography></Stack>
                {decisions.length ? decisions.map((d, i) => <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>• {d}</Typography>) : <Typography variant="body2" color="text.secondary">None captured yet.</Typography>}
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}><RecommendIcon fontSize="small" color="primary" /><Typography fontWeight={700}>Recommendations</Typography></Stack>
                {recommendations.length ? recommendations.map((r, i) => <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>• {r}</Typography>) : <Typography variant="body2" color="text.secondary">None captured yet.</Typography>}
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}><QuestionIcon fontSize="small" color="primary" /><Typography fontWeight={700}>Open questions</Typography></Stack>
                {openQs.length === 0 && <Typography variant="body2" color="text.secondary">No open questions.</Typography>}
                {openQs.map((q) => <QuestionItem key={q.id} q={q} protocolId={id} assignees={assignees} sectionTitles={sectionTitles} canResolve={canResolve} editable={editable} currentUserId={currentUserId} onChanged={reloadAll} />)}
                {editable && (
                  <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                    <TextField size="small" fullWidth placeholder="Add a question" value={newQuestion} onChange={(e) => setNewQuestion(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submitQuestion()} />
                    <IconButton color="primary" onClick={submitQuestion}><AddIcon /></IconButton>
                  </Stack>
                )}
              </Paper>
            </Grid>
          </Grid>

          <ChecklistPanel protocolId={id} />
          <UsagePanel protocolId={id} />
          <HistoryPanel events={history} />
        </Grid>

        {/* Chat */}
        <Grid item xs={12} md={4}>
          {editable ? (
            <ChatPanel messages={messages} sending={sending} value={chatText} onChange={setChatText} onSend={send}
              undoAvailable={undoAvailable} onUndo={undo} busy={busy} activeCountFor={activeCountFor}
              activeCount={activeEdits.length} onKeepAll={() => keepEdits(activeEdits)} onDiscardAll={discardAll} inputRef={chatInputRef}
              protocolId={id} onReviewClick={scrollToReview} />
          ) : (
            <Paper variant="outlined" sx={{ p: 2 }}><Typography variant="body2" color="text.secondary">The AI co-author is available while the protocol is a draft. This version is {head?.state?.replace('_', ' ')}.</Typography></Paper>
          )}
        </Grid>
      </Grid>

      <SubmitDialog open={submitOpen} onClose={() => setSubmitOpen(false)} assignees={assignees} currentUserId={currentUserId}
        onSubmit={async (reviewerId) => {
          try { await submitProtocol(id, reviewerId); toast.success('Submitted for review — reviewer emailed'); setSubmitOpen(false); await reloadAll(); }
          catch (err) { toast.error(err.response?.data?.detail || 'Could not submit'); }
        }} />
      <ReviewCommentsDialog open={commentsOpen} onClose={() => setCommentsOpen(false)} protocolId={id} assignees={assignees} editable={editable}
        onPosted={() => {
          getReviewComments(id).then((d) => setReviewComments(d.comments || [])).catch(() => {});
          getHistory(id).then((h) => setHistory(h.events || [])).catch(() => {});
        }} />

      {/* Selection → add to chat (Cursor-style) */}
      {sel && (
        <Box sx={{ position: 'fixed', left: sel.x, top: sel.y, transform: 'translate(-50%, -100%)', zIndex: 1300 }}>
          <Button size="small" variant="contained" startIcon={<AiIcon />} onClick={addSelToChat} sx={{ boxShadow: 3 }}>Add to chat</Button>
        </Box>
      )}
    </Box>
  );
}
